import {
  type AnyWidget,
  CommonWidgetUtils,
  GRAPH_TYPE,
  HISTOGRAM_RANGE,
  type IChartData,
  type IChartDataset,
  type IRankingWidget,
  type IReportWidget,
  WIDGET_TYPE_RANKING,
  WIDGET_TYPE_REPORT,
} from '@solinkcorp/vd-common';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import {
  OWNER_TYPE,
  WIDGET_REFRESH_LONG_INTERVAL_MS,
  WIDGET_REFRESH_SHORT_INTERVAL_MS,
} from 'vd-constants';

const THRESHOLDABLE_TYPES = [WIDGET_TYPE_REPORT, WIDGET_TYPE_RANKING];

export const LONG_RANKING_SIZE = 10;

export const addEventSubtypeToQuery = ({
  eventSubtypeQuery,
  eqFilter,
  eqMustHaveFields,
  mergeWithSubtypes,
}: {
  eventSubtypeQuery: string;
  eqFilter: { [key: string]: any };
  eqMustHaveFields: { [key: string]: any };
  mergeWithSubtypes?: string[];
}) => {
  if (!eventSubtypeQuery) {
    return;
  }

  if (mergeWithSubtypes && mergeWithSubtypes?.length !== 0) {
    eqFilter.regexp = {
      subtype: { value: eventSubtypeQuery + '|' + (mergeWithSubtypes as string[]).join('|') },
    };
  } else if (eventSubtypeQuery.includes('*')) {
    eqFilter.regexp = { subtype: { value: eventSubtypeQuery } };
  } else {
    eqMustHaveFields.subtype = eventSubtypeQuery;
  }
};

export const isAnyWidgetDataSetEmpty = (dataSets: Array<any>[][]): boolean => {
  if (!dataSets || isEmpty(dataSets)) return true;

  for (const dataSet of dataSets) {
    if (isEmpty(dataSet)) return true;
  }

  return false;
};

export const getWidgetRefreshInterval = (range: string): number => {
  return range === HISTOGRAM_RANGE.HOUR
    ? WIDGET_REFRESH_SHORT_INTERVAL_MS
    : WIDGET_REFRESH_LONG_INTERVAL_MS;
};

/**
 * Calculate the initial/default value for threshold widgets
 * 95% of the max value
 */
export const getDefaultThreshold = (data: IChartData[] | IChartDataset[]) => {
  const maxValue = CommonWidgetUtils.isIChartDataSet(data)
    ? CommonWidgetUtils.getMaxFromChartDataset(data)
    : CommonWidgetUtils.getMaxFromChartData(data);
  return maxValue * 0.95;
};

export const isDateAfterStartOfWeek = (date: number): boolean => {
  return date > moment().startOf('isoWeek').valueOf();
};

/**
 * Get the string for rolling week （last 7 days)
 */
export const getRollingWeek = (date: number | Date): string => {
  return `${moment(date).subtract(6, 'days').format('ddd')} - ${moment(date).format('ddd')}`;
};

export const isResellerOrCustomerWidget = ({
  ownerType,
}: {
  ownerType: AnyWidget['ownerType'];
}) => {
  return ownerType === OWNER_TYPE.RESELLER || ownerType === OWNER_TYPE.CUSTOMER;
};

export const isNumberWidget = (graphType: string) => {
  return graphType === GRAPH_TYPE.NUMBER;
};

// Get the index of the data with the highest absolute value for highlighting.
// If all data values are 0, then highlight the data bucket with the max solinkEventCount.
// This is to show the data bucket that might be of the most interest to the user.
export function getDefaultHighlightDataIndex(data: IChartData[]) {
  let indexOfMaxAbs = 0;
  let indexOfMaxEventCount = 0;
  for (let i = 0; i < data.length; i++) {
    if (Math.abs(data[i].value) > Math.abs(data[indexOfMaxAbs].value)) {
      indexOfMaxAbs = i;
    }
    if (data[i].solinkEventCount > data[indexOfMaxEventCount].solinkEventCount) {
      indexOfMaxEventCount = i;
    }
  }
  return data[indexOfMaxAbs].value === 0 ? indexOfMaxEventCount : indexOfMaxAbs;
}

/**
 * Determine if a widget can be used to create thresholds.
 */
export function canCreateThreshold({
  widgetSettings: { type, graphType },
  thresholds,
}: {
  widgetSettings: {
    type: AnyWidget['widgetSettings']['type'];
    graphType: (IReportWidget | IRankingWidget)['widgetSettings']['graphType'] | undefined;
  };
  thresholds: AnyWidget['thresholds'];
}) {
  return (
    THRESHOLDABLE_TYPES.includes(type) &&
    graphType &&
    !isNumberWidget(graphType) &&
    !thresholds?.length
  );
}
